import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  createEosTodo, updateEosTodo, completeEosTodo, reopenEosTodo,
  cancelEosTodo, deleteEosTodo,
} from '../api';
import { eosTodoKeys } from './useEosTodos';
import type { CreateEosTodoInput, UpdateEosTodoInput } from '../types';

function errMsg(e: unknown): string {
  if (e && typeof e === 'object' && 'message' in e) return String((e as { message: unknown }).message);
  return 'Something went wrong';
}

export function useCreateEosTodo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateEosTodoInput) => createEosTodo(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: eosTodoKeys.all });
      toast.success('To-Do created');
    },
    onError: (e) => toast.error('Failed to create To-Do', { description: errMsg(e) }),
  });
}

export function useUpdateEosTodo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; data: UpdateEosTodoInput }) => updateEosTodo(args.id, args.data),
    onSuccess: (_res, vars) => {
      qc.invalidateQueries({ queryKey: eosTodoKeys.all });
      qc.invalidateQueries({ queryKey: eosTodoKeys.detail(vars.id) });
    },
    onError: (e) => toast.error('Failed to update To-Do', { description: errMsg(e) }),
  });
}

export function useCompleteEosTodo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => completeEosTodo(id),
    onSuccess: (_res, id) => {
      qc.invalidateQueries({ queryKey: eosTodoKeys.all });
      qc.invalidateQueries({ queryKey: eosTodoKeys.detail(id) });
    },
    onError: (e) => toast.error('Failed to complete To-Do', { description: errMsg(e) }),
  });
}

export function useReopenEosTodo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => reopenEosTodo(id),
    onSuccess: (_res, id) => {
      qc.invalidateQueries({ queryKey: eosTodoKeys.all });
      qc.invalidateQueries({ queryKey: eosTodoKeys.detail(id) });
    },
    onError: (e) => toast.error('Failed to reopen To-Do', { description: errMsg(e) }),
  });
}

export function useCancelEosTodo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => cancelEosTodo(id),
    onSuccess: (_res, id) => {
      qc.invalidateQueries({ queryKey: eosTodoKeys.all });
      qc.invalidateQueries({ queryKey: eosTodoKeys.detail(id) });
    },
    onError: (e) => toast.error('Failed to cancel To-Do', { description: errMsg(e) }),
  });
}

export function useDeleteEosTodo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteEosTodo(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: eosTodoKeys.all });
      toast.success('To-Do deleted');
    },
    onError: (e) => toast.error('Failed to delete To-Do', { description: errMsg(e) }),
  });
}
